import {
  DashboardState,
  IAction,
  IDocumentParameterDefinition,
  IDocumentParameterInput,
  IEvent
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { ProjectSettingsService } from "../../../services/project-settings-service";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

/**
 * State for the Document Parameters tool (KOA-17254).
 *
 * <b>Source actions are real designer actions.</b> They are added through the right panel's normal action picker,
 * exactly as actions are added to a screen's PageInit, so each one is a full `IAction` with its own parameters
 * and symbols. That is why they live in an `IEvent` here rather than in a reduced "action reference" shape: the
 * picker, the action panels and the paste/reorder handlers all speak `IEvent`/`IAction`, and giving them anything
 * else would mean reimplementing the whole surface.
 *
 * Storage is the project settings section (`KProjectSettings.documentParameters`), which is loaded and saved as a
 * unit. Where the data lives is unrelated to where it is edited.
 */
export interface DocumentParametersProviderState {
  /** The added source actions, shaped as an event so the action picker can drive it directly. */
  documentParameterEvent: IEvent;
  documentParameterDefinitions: IDocumentParameterDefinition[];
  /**
   * Values the project declares as coming from outside. The symbol picker offers these when wiring a source
   * action's parameter, and the call site binds exactly this list.
   */
  documentParameterInputs: IDocumentParameterInput[];
  isDocumentParametersLoading: boolean;
  /**
   * The tool is also reachable as a popup from a Document component's properties, so "am I authoring document
   * parameters" is no longer the same question as "is the dashboard in the documentParameters state".
   */
  isDocumentParametersPopupOpen: boolean;
  openDocumentParametersPopup: () => void;
  closeDocumentParametersPopup: () => void;
  /**
   * True on any surface that is authoring document parameters — the tool or the popup.
   *
   * <b>Read this instead of comparing `dashboardState`.</b> The action picker, the action panel and the shared
   * action-definition filter all decide what to offer from the surface they are on, and every one of them asked
   * `dashboardState === documentParameters`. Opening the same authoring UI in a popup over the design canvas left
   * all of them answering for the design canvas: no source-action list, no host event, and the filter offering
   * navigation and UI actions that cannot supply a value. The popup would have rendered and done nothing.
   */
  isDocumentParameterAuthoringActive: () => boolean;
  loadDocumentParameters: (projectId: string) => void;
  /** Adds or replaces a source action. Called by the action picker through saveActionToEvent. */
  addActionToDocumentParameters: (action: IAction) => void;
  deleteDocumentParameterAction: (action: IAction) => void;
  reorderDocumentParameterActions: (sourceIndex: number, destinationIndex: number) => void;
  /**
   * Commits an edited definition list. The list is passed in rather than read back from state, because a form
   * that sets state and saves in the same handler would otherwise persist the pre-edit list — React has not
   * applied the update yet at that point, and the save would report success while writing nothing.
   */
  saveDocumentParameterDefinitions: (
    definitions: IDocumentParameterDefinition[],
    inputs: IDocumentParameterInput[]
  ) => Promise<void>;
}

/** Event name for the synthetic host event, mirroring "scheduledJobInit" / "apiMethodInit". */
export const DOCUMENT_PARAMETERS_EVENT_NAME: string = "documentParametersInit";

export class DocumentParametersProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  DocumentParametersProviderState
> {
  static defaultState: DocumentParametersProviderState = {
    documentParameterEvent: {
      eventName: DOCUMENT_PARAMETERS_EVENT_NAME,
      actions: []
    } as unknown as IEvent,
    documentParameterDefinitions: [],
    documentParameterInputs: [],
    isDocumentParametersLoading: false,
    isDocumentParametersPopupOpen: false,
    openDocumentParametersPopup: () => {},
    closeDocumentParametersPopup: () => {},
    isDocumentParameterAuthoringActive: () => false,
    loadDocumentParameters: () => {},
    addActionToDocumentParameters: () => {},
    deleteDocumentParameterAction: () => {},
    reorderDocumentParameterActions: () => {},
    saveDocumentParameterDefinitions: () => new Promise(() => {})
  };

  getProviderValues = (): DocumentParametersProviderState => {
    return {
      documentParameterEvent: this.state.documentParameterEvent,
      documentParameterDefinitions: this.state.documentParameterDefinitions,
      documentParameterInputs: this.state.documentParameterInputs,
      isDocumentParametersLoading: this.state.isDocumentParametersLoading,
      isDocumentParametersPopupOpen: this.state.isDocumentParametersPopupOpen,
      openDocumentParametersPopup: this.openDocumentParametersPopup,
      closeDocumentParametersPopup: this.closeDocumentParametersPopup,
      isDocumentParameterAuthoringActive: this.isDocumentParameterAuthoringActive,
      loadDocumentParameters: this.loadDocumentParameters,
      addActionToDocumentParameters: this.addActionToDocumentParameters,
      deleteDocumentParameterAction: this.deleteDocumentParameterAction,
      reorderDocumentParameterActions: this.reorderDocumentParameterActions,
      saveDocumentParameterDefinitions: this.saveDocumentParameterDefinitions
    };
  };

  isDocumentParameterAuthoringActive = (): boolean =>
    this.state.dashboardState === DashboardState.documentParameters || this.state.isDocumentParametersPopupOpen;

  /**
   * The popup loads what it edits. It is opened from a Document component on the design canvas, where nothing has
   * fetched the definitions, the declared inputs or the source actions — the tool's own canvas does that on mount
   * and this surface has no such mount to inherit from.
   */
  openDocumentParametersPopup = (): void => {
    this.setState({ isDocumentParametersPopupOpen: true }, () => {
      this.loadDocumentParameters(this.state.project?.id ?? "");
    });
  };

  closeDocumentParametersPopup = (): void => {
    this.setState({ isDocumentParametersPopupOpen: false });
  };

  loadDocumentParameters = (projectId: string): void => {
    if (!projectId) return;

    this.setState({ isDocumentParametersLoading: true });
    ProjectSettingsService.getDocumentParameters(projectId)
      .then((response) => {
        const actions: IAction[] = response?.data?.actions ?? [];
        this.setState({
          documentParameterEvent: { eventName: DOCUMENT_PARAMETERS_EVENT_NAME, actions } as unknown as IEvent,
          documentParameterDefinitions: response?.data?.definitions ?? [],
          documentParameterInputs: response?.data?.inputs ?? [],
          isDocumentParametersLoading: false
        });
      })
      .catch((error: unknown) => {
        KMainFunctions.exceptionHandler(error);
        this.setState({ isDocumentParametersLoading: false });
      });
  };

  /** A fresh, complete host event — used when state has not been populated yet. */
  private emptyEvent = (): IEvent =>
    ({ eventName: DOCUMENT_PARAMETERS_EVENT_NAME, actions: [] as IAction[] }) as unknown as IEvent;

  addActionToDocumentParameters = (action: IAction): void => {
    if (!action) return;

    const event: IEvent = _.cloneDeep(this.state.documentParameterEvent) ?? this.emptyEvent();
    if (!event.actions) event.actions = [];

    // Replace in place when the picker is saving an edit to an action that is already there; the picker calls the
    // same entry point for add and edit.
    const existingIndex: number = event.actions.findIndex((candidate: IAction) => candidate.id === action.id);
    if (existingIndex > -1) {
      event.actions[existingIndex] = action;
    } else {
      event.actions.push(action);
    }

    this.setState({ documentParameterEvent: event }, () => this.saveDocumentParameters());
  };

  deleteDocumentParameterAction = (action: IAction): void => {
    if (!action) return;

    const event: IEvent = _.cloneDeep(this.state.documentParameterEvent) ?? this.emptyEvent();
    event.actions = (event.actions ?? []).filter((candidate: IAction) => candidate.id !== action.id);

    // Parameters whose value came from the removed action would be saved as dangling references, which the backend
    // rejects. Clearing the value keeps the save valid and surfaces the gap in the parameter row instead.
    const removedActionDefinitionId: string | undefined = action.actionDefinitionId;
    const definitions: IDocumentParameterDefinition[] = this.state.documentParameterDefinitions.map(
      (definition: IDocumentParameterDefinition) => {
        const symbol = definition.symbol as { actionDefinitionId?: string } | undefined;
        if (removedActionDefinitionId && symbol?.actionDefinitionId === removedActionDefinitionId) {
          return { ...definition, symbol: undefined };
        }
        return definition;
      }
    );

    this.setState({ documentParameterEvent: event, documentParameterDefinitions: definitions }, () =>
      this.saveDocumentParameters()
    );
  };

  reorderDocumentParameterActions = (sourceIndex: number, destinationIndex: number): void => {
    const event: IEvent = _.cloneDeep(this.state.documentParameterEvent) ?? this.emptyEvent();
    const actions: IAction[] = event.actions ?? [];
    if (
      sourceIndex < 0 ||
      destinationIndex < 0 ||
      sourceIndex >= actions.length ||
      destinationIndex >= actions.length
    ) {
      return;
    }

    const [moved] = actions.splice(sourceIndex, 1);
    actions.splice(destinationIndex, 0, moved);
    event.actions = actions;

    this.setState({ documentParameterEvent: event }, () => this.saveDocumentParameters());
  };

  saveDocumentParameterDefinitions = (
    definitions: IDocumentParameterDefinition[],
    inputs: IDocumentParameterInput[]
  ): Promise<void> =>
    new Promise<void>((resolve: () => void, reject: (reason: unknown) => void) => {
      // Written from the setState callback, so the request carries the lists the form just edited. Reading them
      // back from state in the caller's turn would persist the pre-edit values and report success for a write that
      // silently dropped the new parameter.
      this.setState({ documentParameterDefinitions: definitions, documentParameterInputs: inputs }, () => {
        this.persist().then(resolve).catch(reject);
      });
    });

  /** The single write path: every part of the section goes out together, so a partial save is not expressible. */
  private persist = async (): Promise<void> => {
    const projectId: string | undefined = this.state.project?.id;
    if (!projectId) {
      // Returning quietly here used to make a lost project id indistinguishable from a successful save. Every
      // "it does not save" report on this surface has been a write that failed without saying so.
      throw new Error("No project is loaded, so document parameters cannot be saved.");
    }

    await ProjectSettingsService.upsertDocumentParameters(projectId, {
      // Sent as authored: the backend stores a source action opaquely, so a round trip cannot strip the
      // action's own parameters or label.
      actions: this.state.documentParameterEvent?.actions ?? [],
      inputs: this.state.documentParameterInputs ?? [],
      definitions: this.state.documentParameterDefinitions
    });
  };

  /**
   * Fire-and-forget save for source-action edits. Those are already committed from a setState callback, and the
   * picker has no place to surface a rejected promise, so a failure is reported here.
   */
  private saveDocumentParameters = (): void => {
    this.persist().catch((error: unknown) => KMainFunctions.exceptionHandler(error));
  };
}
